import { OrganizationCard, UserCard } from "@/components/cards";
import { useMembers } from "@/hooks/use-members";
import { usePartnerships } from "@/hooks/use-partnerships";
import { Combobox } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { guardArray } from "@msuite/utils/array";
import type { TicketFormSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

export function TicketFormAssignee() {
	/** Context */
	const methods = useFormContext<TicketFormSchema>();
	const { t } = useTranslation();
	const { members } = useMembers();
	const { partnerships } = usePartnerships("host");

	const options = useMemo(() => {
		const _members = guardArray(
			members?.map((member) => ({
				type: "member" as const,
				item: member,
			})),
		);
		const _partnerships = guardArray(
			partnerships?.map((partnership) => ({
				type: "partnership" as const,
				item: partnership,
			})),
		);
		return [..._members, ..._partnerships];
	}, [members, partnerships]);

	/** Render */
	return (
		<Combobox
			control={methods.control}
			name="assignee"
			helperText={t("tasks.assignee_helper_text")}
			isRequired={false}
			label={t("tasks.assignee")}
			options={options}
			keyExtractor={(item) => item?.item?.id}
			data-cy="ticket-form-assignee-select"
			displayValue={(item) => {
				if (item.type === "member") return getFullName(item.item.user);
				return item.item._partner.name;
			}}
			placeholder={t("general.select")}
			renderOption={(item) => {
				if (item.type === "member") {
					return (
						<UserCard
							variant="plain"
							withPopover={false}
							userId={item.item.user.id}
						/>
					);
				} else {
					return (
						<OrganizationCard
							variant="plain"
							orgId={item.item.partnerOrgId}
							withPopover={false}
						/>
					);
				}
			}}
		/>
	);
}
